import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Shield, Plus, Edit2, Trash2, ChevronDown, ChevronRight, Save, X, Check
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const Roles = () => {
  const { user: loggedInUser } = useContext(AuthContext);

  if (loggedInUser.profile !== "admin") {
    return <Redirect to="/" />;
  }
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedRole, setExpandedRole] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissionIds: []
  });

  const canEdit = loggedInUser?.permissions?.includes("*") || loggedInUser?.permissions?.includes("roles:edit");
  const canCreate = loggedInUser?.permissions?.includes("*") || loggedInUser?.permissions?.includes("roles:create");
  const canDelete = loggedInUser?.permissions?.includes("*") || loggedInUser?.permissions?.includes("roles:delete");

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roles");
      setRoles(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const fetchPermissions = async () => {
    try {
      const { data } = await api.get("/permissions");
      setAllPermissions(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, formData);
        toast.success("Rol actualizado");
      } else {
        await api.post("/roles", formData);
        toast.success("Rol creado");
      }
      setShowForm(false);
      setEditingRole(null);
      setFormData({ name: "", description: "", permissionIds: [] });
      fetchRoles();
    } catch (err) {
      toastError(err);
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || "",
      permissionIds: role.permissions?.map(p => p.id) || []
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deletingRole) return;
    try {
      await api.delete(`/roles/${deletingRole.id}`);
      toast.success("Rol eliminado");
      fetchRoles();
    } catch (err) {
      toastError(err);
    }
    setDeletingRole(null);
    setConfirmOpen(false);
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permId)
        ? prev.permissionIds.filter(id => id !== permId)
        : [...prev.permissionIds, permId]
    }));
  };

  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) acc[perm.resource] = [];
    acc[perm.resource].push(perm);
    return acc;
  }, {});

  const resourceLabels = {
    tickets: "Tickets",
    messages: "Mensajes",
    contacts: "Contactos",
    connections: "Conexiones",
    queues: "Colas",
    users: "Usuarios",
    settings: "Configuracion",
    quickAnswers: "Respuestas rapidas",
    bot: "Bot",
    dashboard: "Dashboard",
    auditLogs: "Auditoria",
    roles: "Roles",
    statistics: "Estadisticas",
    appointments: "Citas",
    billing: "Facturacion"
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={`Eliminar rol "${deletingRole?.name}"?`}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        Esta accion no se puede deshacer. Los usuarios con este rol perderan sus permisos.
      </ConfirmationModal>

      <MainHeader>
        <Title>Roles y Permisos</Title>
        <MainHeaderButtonsWrapper>
          {canCreate && (
            <button
              onClick={() => {
                setEditingRole(null);
                setFormData({ name: "", description: "", permissionIds: [] });
                setShowForm(true);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Nuevo Rol
            </button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6">
        {showForm && (
          <div className="mb-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {editingRole ? `Editar: ${editingRole.name}` : "Nuevo Rol"}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingRole(null); }} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={editingRole?.isSystem}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Descripcion</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Permisos</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(groupedPermissions).map(([resource, perms]) => (
                  <div key={resource} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-2 uppercase">
                      {resourceLabels[resource] || resource}
                    </p>
                    <div className="space-y-1.5">
                      {perms.map(perm => (
                        <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.permissionIds.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-xs text-slate-700 dark:text-slate-300">{perm.description || perm.action}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingRole(null); }}
                className="px-4 py-2 text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingRole ? "Actualizar" : "Crear"}
              </button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full text-sm min-w-[650px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                {["", "Nombre", "Descripcion", "Permisos", "Sistema", "Acciones"].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap ${i === 1 ? "text-left" : "text-center"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {roles.length === 0 && !loading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <p className="text-base font-semibold text-slate-500 dark:text-slate-400">No hay roles</p>
                  </td>
                </tr>
              ) : (
                roles.map((role) => (
                  <React.Fragment key={role.id}>
                    <tr
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors cursor-pointer"
                      onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
                    >
                      <td className="px-4 py-3 text-center">
                        {expandedRole === role.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-indigo-500" />
                          <span className="font-medium text-slate-900 dark:text-slate-100">{role.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-500 dark:text-slate-400">{role.description || "—"}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                          {role.permissions?.length || 0} permisos
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {role.isSystem ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Sistema</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {canEdit && (
                            <button onClick={(e) => { e.stopPropagation(); handleEdit(role); }} title="Editar" className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors">
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && !role.isSystem && (
                            <button onClick={(e) => { e.stopPropagation(); setDeletingRole(role); setConfirmOpen(true); }} title="Eliminar" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRole === role.id && (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 bg-slate-50 dark:bg-slate-800/30">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                            {role.permissions?.map(perm => (
                              <div key={perm.id} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                                <Check className="w-3 h-3 text-emerald-500" />
                                <span>{perm.name}</span>
                              </div>
                            ))}
                            {(!role.permissions || role.permissions.length === 0) && (
                              <p className="text-xs text-slate-400 col-span-4">Sin permisos asignados</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
              {loading && (
                <tr>
                  <td colSpan={6} className="py-4 text-center">
                    <div className="flex justify-center"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainContainer>
  );
};

export default Roles;
